// The map legend (iOS MapLegendCard / MapLegendRow): "BY STAGE" header, one swatch + label + count row per legend
// entry in the iOS order, collapsible. iOS shows 7 rows then "+N more"; on a desk-sized screen "+N more" expands the
// list in place (scrolling) instead, because owners with many reps want every name.

import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { MAP_COLORIZE_TITLES, type MapColorize } from '@/contracts/enums';
import type { LegendEntry } from '@/domain/records';
import { formatGrouped } from '@/domain/format';
import { ColorDot, cn } from '@/ui';

const MAX_ROWS = 7;

export interface MapLegendProps {
  mode: MapColorize;
  entries: readonly LegendEntry[];
  /** Extra rows under the entries (the territory boundary swatch on the territory map). */
  footer?: ReactNode;
  /** Text when there are no pins (iOS "No pins in view"). */
  emptyText?: string;
  defaultExpanded?: boolean;
  className?: string;
}

export function MapLegend({ mode, entries, footer, emptyText = 'No pins on the map', defaultExpanded = true, className }: MapLegendProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);
  const bodyId = useId();
  const rows = showAll ? entries : entries.slice(0, MAX_ROWS);
  const hidden = entries.length - rows.length;
  const title = `By ${MAP_COLORIZE_TITLES[mode]}`;

  return (
    <section
      aria-label={`Legend, ${title}`}
      className={cn(
        'pointer-events-auto w-[220px] rounded-tile border border-line bg-surface/95 p-3 shadow-raised backdrop-blur',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <h2 className="min-w-0 flex-1 truncate text-[10px] font-bold tracking-[0.06em] text-accent uppercase">{title}</h2>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          aria-label={expanded ? 'Collapse legend' : 'Expand legend'}
          onClick={() => setExpanded((v) => !v)}
          className="flex size-6 items-center justify-center rounded-full text-fg-muted hover:bg-surface-3 focus-visible:outline-2 focus-visible:outline-focus"
        >
          <ChevronDown aria-hidden className={cn('size-3.5 transition-transform', expanded ? '' : 'rotate-180')} strokeWidth={2.5} />
        </button>
      </div>
      <div id={bodyId} hidden={!expanded} className="mt-2">
        {entries.length === 0 ? (
          <p className="text-xs text-fg-muted">{emptyText}</p>
        ) : (
          <ul className={cn('flex flex-col gap-1.5', showAll && 'max-h-[40vh] overflow-y-auto pr-1 gt-scroll')}>
            {rows.map((e) => (
              <li key={e.label} className="flex items-center gap-2 text-xs">
                <ColorDot color={e.colorHex} size={10} className="ring-1 ring-white dark:ring-surface" />
                <span className="min-w-0 flex-1 truncate text-fg" title={e.label}>
                  {e.label}
                </span>
                <span className="font-semibold text-fg-muted tabular-nums">{formatGrouped(e.count)}</span>
              </li>
            ))}
          </ul>
        )}
        {(hidden > 0 || showAll) && entries.length > MAX_ROWS && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mt-1.5 rounded text-xs font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-focus"
          >
            {showAll ? 'Show fewer' : `+${formatGrouped(hidden)} more`}
          </button>
        )}
        {footer != null && <div className="mt-2 border-t border-line pt-2">{footer}</div>}
      </div>
    </section>
  );
}
